import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../services/supabaseClient';
import { Youtube, Facebook, AlertTriangle, RefreshCw } from 'lucide-react';

// Custom TikTok icon component
const TikTokIcon = ({ size = 24, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Custom Instagram icon component
const InstagramIcon = ({ size = 24, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

function ConnectionsTab({ 
  connectedPlatforms, 
  onConnect, 
  onDisconnect,
  tiktokCreatorInfo,
  tiktokSettings,
  onTiktokSettingsChange,
  facebookPages,
  selectedFacebookPage,
  onSelectFacebookPage
}) {
  const { t } = useTranslation();
  const [savingFbPage, setSavingFbPage] = useState(false);
  const [tokenExpirationInfo, setTokenExpirationInfo] = useState({});

  // Fetch token expiration data from Supabase
  useEffect(() => {
    const fetchTokenExpiration = async () => {
      const session = supabase.getSession();
      if (!session?.user?.id) return;

      try {
        const tokens = await supabase.fetchPlatformTokens(session.user.id);
        const expirationInfo = {};

        tokens.forEach(token => {
          if (token.expires_at) {
            const expiresAt = new Date(token.expires_at);
            const now = new Date();
            const isExpired = expiresAt <= now;
            const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
            
            // For YouTube/Google, check if refresh token exists
            // If it does, the token can auto-refresh, so don't mark as expired
            // TikTok also supports refresh tokens, treat it the same way
            const hasRefreshToken = !!token.refresh_token;
            const supportsAutoRefresh = token.platform === 'youtube' || token.platform === 'google' || token.platform === 'tiktok';
            const actuallyExpired = isExpired && !(supportsAutoRefresh && hasRefreshToken);
            
            expirationInfo[token.platform] = {
              expires_at: token.expires_at,
              expiresAt: expiresAt,
              expired: actuallyExpired,
              daysUntilExpiry: daysUntilExpiry,
              hasRefreshToken: hasRefreshToken,
              message: actuallyExpired 
                ? t('app.connections.tokenExpired') 
                : supportsAutoRefresh && hasRefreshToken && isExpired
                  ? t('app.connections.tokenAutoRefreshed')
                  : t('app.connections.tokenExpires', { date: expiresAt.toLocaleDateString('hu-HU'), days: daysUntilExpiry })
            };
          } else {
            // Token without expiration (possibly doesn't expire or refresh token)
            expirationInfo[token.platform] = {
              expires_at: null,
              expired: false,
              message: t('app.connections.noExpiryDate')
            };
          }
        });

        setTokenExpirationInfo(expirationInfo);
      } catch (error) {
        console.error('Failed to fetch token expiration:', error);
      }
    };

    fetchTokenExpiration();
    // Check every minute
    const interval = setInterval(fetchTokenExpiration, 60000);
    return () => clearInterval(interval);
  }, [connectedPlatforms, t]);

  const platformConfig = [
    { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'red' },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'blue' },
    { id: 'instagram', name: 'Instagram', icon: InstagramIcon, color: 'pink' },
    { id: 'tiktok', name: 'TikTok', icon: TikTokIcon, color: 'black' }
  ];

  const colorClasses = {
    red: {
      icon: 'text-red-500',
      connectedText: 'text-red-600',
      connectedBorder: 'border-red-600',
      connectBg: 'bg-red-500',
      connectHover: 'hover:bg-red-600',
      disconnectHoverBg: 'hover:bg-red-50'
    },
    blue: {
      icon: 'text-blue-500',
      connectedText: 'text-blue-600',
      connectedBorder: 'border-blue-600',
      connectBg: 'bg-blue-500',
      connectHover: 'hover:bg-blue-600',
      disconnectHoverBg: 'hover:bg-blue-50'
    },
    pink: {
      icon: 'text-pink-500',
      connectedText: 'text-pink-600',
      connectedBorder: 'border-pink-600',
      connectBg: 'bg-pink-500',
      connectHover: 'hover:bg-pink-600',
      disconnectHoverBg: 'hover:bg-pink-50'
    },
    black: {
      icon: 'text-black',
      connectedText: 'text-gray-800',
      connectedBorder: 'border-black',
      connectBg: 'bg-black',
      connectHover: 'hover:bg-gray-800',
      disconnectHoverBg: 'hover:bg-gray-50'
    }
  };

  // Check if token is expiring soon (within 7 days)
  const isExpiringSoon = (platformId) => {
    const info = tokenExpirationInfo[platformId];
    if (!info || info.expired || !info.daysUntilExpiry) return false;
    // If the platform has a refresh token, it will auto-refresh, so don't show warning
    if (info.hasRefreshToken) return false;
    return info.daysUntilExpiry <= 7;
  };

  // Check if Instagram is available (requires Facebook connection with linked Instagram account)
  const isInstagramAvailable = () => {
    // Instagram is only available if Facebook is connected AND has a linked Instagram account
    if (!connectedPlatforms.facebook) return false;
    return !!selectedFacebookPage?.instagram_business_account;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 max-w-3xl">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('app.connections.title')}</h2>
      <div className="space-y-4">
        {platformConfig.map(({ id, name, icon: Icon, color }) => {
          const expirationInfo = tokenExpirationInfo[id];
          const expiringSoon = isExpiringSoon(id);
          const isExpired = expirationInfo?.expired;
          
          // Special handling for Instagram - only available through Facebook
          const isInstagramUnavailable = id === 'instagram' && !isInstagramAvailable();
          
          return (
          <div key={id} className={`flex flex-col p-5 border-2 rounded-lg ${
            isInstagramUnavailable ? 'border-gray-200 bg-gray-50 opacity-75' :
            isExpired ? 'border-red-300 bg-red-50' : 
            expiringSoon ? 'border-yellow-300 bg-yellow-50' : 
            'border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Icon className={`${isInstagramUnavailable ? 'text-gray-400' : colorClasses[color]?.icon || 'text-gray-500'}`} size={32} />
                <div>
                  <h3 className={`font-semibold ${isInstagramUnavailable ? 'text-gray-500' : 'text-gray-800'}`}>{name}</h3>
                  <p className="text-sm text-gray-500">
                    {isInstagramUnavailable ? t('app.connections.notAvailable') : 
                    connectedPlatforms[id] ? t('app.connections.connected') : t('app.connections.notConnected')}
                  </p>
                  {/* Token expiration info for all platforms */}
                  {connectedPlatforms[id] && expirationInfo && !isInstagramUnavailable && (
                    <p className={`text-xs mt-1 ${
                      isExpired ? 'text-red-600 font-medium' : 
                      expiringSoon ? 'text-yellow-600' : 
                      'text-gray-400'
                    }`}>
                      {isExpired ? (
                        <span className="flex items-center gap-1">
                          <AlertTriangle size={12} />
                          {expirationInfo.message}
                        </span>
                      ) : (
                        expirationInfo.message
                      )}
                    </p>
                  )}
                  {/* Instagram unavailable note */}
                  {isInstagramUnavailable && (
                    <p className="text-xs mt-1 text-gray-500">
                      
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Instagram unavailable - no button */}
                {isInstagramUnavailable ? null : (
                  <>
                {/* Re-authenticate button for expired/expiring tokens */}
                {connectedPlatforms[id] && (isExpired || expiringSoon) && (
                  <button
                    type="button"
                    onClick={() => onConnect(id)}
                    className={`px-3 py-2 text-sm rounded-lg flex items-center gap-1 transition-colors cursor-pointer ${
                      isExpired 
                        ? 'bg-red-500 text-white hover:bg-red-600' 
                        : 'bg-yellow-500 text-white hover:bg-yellow-600'
                    }`}
                    title={t('app.connections.renewToken')}
                  >
                    <RefreshCw size={14} />
                    {t('app.connections.renew')}
                  </button>
                )}
                {connectedPlatforms[id] ? (
              <button
                type="button"
                onClick={() => { console.log('disconnect clicked', id); onDisconnect(id); }}
                className={`px-4 py-2 ${colorClasses[color]?.connectedText || 'text-gray-700'} ${colorClasses[color]?.connectedBorder || 'border-gray-300'} border rounded-lg ${colorClasses[color]?.disconnectHoverBg || 'hover:bg-gray-50'} transition-colors cursor-pointer`}
              >
                {t('app.connections.disconnect')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { console.log('connect clicked', id); onConnect(id); }}
                className={`px-4 py-2 ${colorClasses[color]?.connectBg || 'bg-gray-500'} text-white rounded-lg ${colorClasses[color]?.connectHover || 'hover:bg-gray-600'} transition-colors cursor-pointer`}
              >
                {t('app.connections.connect')}
              </button>
            )}
                  </>
                )}
              </div>
            </div>
            
            {/* Instagram unavailable info banner */}
            {isInstagramUnavailable && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  {t('app.connections.instagramRequirement')}</p>
              </div>
            )}
            
            {/* Warning banner for expired tokens */}
            {connectedPlatforms[id] && isExpired && (
              <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle size={16} />
                  <span>{t('app.connections.expiredWarning', { name })}</span>
                </p>
              </div>
            )}
            
            {/* Warning banner for expiring soon tokens */}
            {connectedPlatforms[id] && expiringSoon && !isExpired && (
              <div className="mt-3 p-3 bg-yellow-100 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-700 flex items-center gap-2">
                  <AlertTriangle size={16} />
                  <span>{t('app.connections.expiringWarning', { name })}</span>
                </p>
              </div>
            )}
            
            {/* Facebook Page Selector - show when Facebook is connected and pages exist */}
            {id === 'facebook' && connectedPlatforms[id] && facebookPages && facebookPages.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('app.connections.facebookPage')}
                </label>
                <select
                  value={selectedFacebookPage?.id || ''}
                  onChange={async (e) => {
                    const page = facebookPages.find(p => p.id === e.target.value);
                    if (page && onSelectFacebookPage) {
                      setSavingFbPage(true);
                      await onSelectFacebookPage(page); // This will save immediately
                      setSavingFbPage(false);
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none bg-white"
                  disabled={savingFbPage || facebookPages.length === 1}
                >
                  {facebookPages.map((page) => (
                    <option key={page.id} value={page.id}>
                      {page.name}
                      {page.instagram_business_account ? t('app.connections.instagramLinkedSuffix') : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {facebookPages.length === 1 
                    ? t('app.connections.singlePageHint') 
                    : t('app.connections.selectPageHint')}
                </p>
              </div>
            )}

            {/* TikTok Settings - Hidden: now shown on Upload tab instead
            {id === 'tiktok' && connectedPlatforms[id] && tiktokCreatorInfo && (
              <div className="mt-4">
                <TikTokSettings
                  creatorInfo={tiktokCreatorInfo}
                  settings={tiktokSettings}
                  onSettingsChange={onTiktokSettingsChange}
                  videoType="video"
                />
              </div>
            )}
            */}
          </div>
          );
        })}
      </div>
    </div>
  );
}

export default ConnectionsTab;